/**
 * Romanized Nepali → Devanagari transliteration.
 *
 * Typing `nepal` yields नेपाल, `namaste` yields नमस्ते.
 *
 * The scheme is phonetic and forgiving rather than a strict ISO standard,
 * because writers type the way they text. The rules that matter:
 *
 *   - Consonants carry an inherent 'a' (क = ka), so `kam` → कम, not क्अम्.
 *   - A consonant followed by another consonant takes a halanta: `namaste`
 *     → न + म + स् + ते.
 *   - A word-final consonant does NOT take a halanta, because Nepali drops the
 *     schwa there: `nepal` → नेपाल (ल, not ल्). Type `\` to force one: `sathi\`.
 *   - Capitals select the retroflex/aspirate series where Nepali distinguishes
 *     pairs an ASCII keyboard cannot: T=ट t=त, D=ड d=द, N=ण n=न, S/Sh=ष sh=श.
 *   - Word-initial vowels use the independent letters (अ आ इ), elsewhere the
 *     matra (ा ि ी).
 *
 * Everything is longest-match-first, so `chh` beats `ch` beats `c`.
 */

/** Consonants, including the conjunct shortcuts writers expect. */
const CONSONANTS: Record<string, string> = {
  k: 'क',
  kh: 'ख',
  g: 'ग',
  gh: 'घ',
  ng: 'ङ',
  ch: 'च',
  c: 'च',
  chh: 'छ',
  Ch: 'छ',
  j: 'ज',
  jh: 'झ',
  ny: 'ञ',
  T: 'ट',
  Th: 'ठ',
  D: 'ड',
  Dh: 'ढ',
  N: 'ण',
  t: 'त',
  th: 'थ',
  d: 'द',
  dh: 'ध',
  n: 'न',
  p: 'प',
  ph: 'फ',
  f: 'फ',
  b: 'ब',
  bh: 'भ',
  m: 'म',
  y: 'य',
  r: 'र',
  l: 'ल',
  // U+0933 LLA. Rare in Nepali but present in names and Marathi-derived words,
  // and unreachable otherwise since lowercase l is already ल.
  L: 'ळ',
  v: 'व',
  w: 'व',
  sh: 'श',
  S: 'ष',
  Sh: 'ष',
  shh: 'ष',
  s: 'स',
  h: 'ह',
  // Conjuncts common enough to deserve their own key. Without these the rules
  // still produce a correct halanta form, but these are the spellings writers
  // actually reach for.
  ksh: 'क्ष',
  kchh: 'क्ष',
  tr: 'त्र',
  gy: 'ज्ञ',
  gny: 'ज्ञ',
  jn: 'ज्ञ',
  shr: 'श्र',
  shra: 'श्र',
  dny: 'ज्ञ',
  // Nukta forms used in loanwords and Urdu-derived vocabulary.
  z: 'ज़',
  Z: 'ज़',
  q: 'क़',
  Kh: 'ख़',
  G: 'ग़',
  F: 'फ़',
  x: 'क्स',
  R: 'ड़',
  Rh: 'ढ़',
};

/** [independent, matra] */
const VOWELS: Record<string, [string, string]> = {
  a: ['अ', ''],
  aa: ['आ', 'ा'],
  A: ['आ', 'ा'],
  i: ['इ', 'ि'],
  ee: ['ई', 'ी'],
  ii: ['ई', 'ी'],
  I: ['ई', 'ी'],
  u: ['उ', 'ु'],
  oo: ['ऊ', 'ू'],
  uu: ['ऊ', 'ू'],
  U: ['ऊ', 'ू'],
  e: ['ए', 'े'],
  ai: ['ऐ', 'ै'],
  ei: ['ऐ', 'ै'],
  o: ['ओ', 'ो'],
  au: ['औ', 'ौ'],
  ou: ['औ', 'ौ'],
  // `ri` is deliberately absent: it collides with र + ि (रिता), which is far
  // more common than ऋ. Writers reach ऋ with a capital.
  Ri: ['ऋ', 'ृ'],
  RI: ['ऋ', 'ृ'],
  // U+0960 vocalic RR. `RR` cannot be confused with anything — R alone is ड़,
  // and the matcher takes the longest key.
  RR: ['ॠ', 'ॄ'],
  RRi: ['ॠ', 'ॄ'],
};

/**
 * Candra vowels, reachable only from the palette.
 *
 * ऑ/ॉ (U+0911/U+0949) carry English loanwords — डॉक्टर, ऑफिस — and ऍ/ॅ
 * (U+090D/U+0945) the same for Marathi-influenced spellings. They get no
 * typing key on purpose: the natural romanisations ("aw", "ae") collide with
 * ordinary words — `awasar` would open on ऑ instead of अ — and silently
 * mangling common words to reach a rare one is a bad trade.
 */

const HALANTA = '्';
const ANUSVARA = 'ं';
const CHANDRABINDU = 'ँ';
const VISARGA = 'ः';

/** Standalone marks and punctuation. */
const SIGNS: Record<string, string> = {
  M: ANUSVARA,
  '~': CHANDRABINDU,
  '^': CHANDRABINDU,
  H: VISARGA,
  // A trailing backslash forces the halanta the word-final rule would drop.
  '\\': HALANTA,
};

/**
 * Whole words that map to a single symbol.
 *
 * Checked before the rules, so "om" still spells ओम — only the explicit capital
 * form reaches for the symbol. Guessing here would be worse than not offering
 * it: ओम is a name, ॐ is not.
 */
const SYMBOL_WORDS: Record<string, string> = {
  OM: 'ॐ',
  AUM: 'ॐ',
  RS: '₹',
};

const DEVANAGARI_DIGITS = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];

/**
 * Common words whose correct spelling carries vowel length the roman shorthand
 * drops. Rules alone turn `nepal` into नेपल — phonetically defensible, but not
 * how anyone writes it. This layer is checked first, so writers get नेपाल
 * without having to type `nepaal`.
 *
 * Keep it to genuinely high-frequency words; the rule engine handles the rest.
 */
const DICTIONARY: Record<string, string> = {
  nepal: 'नेपाल',
  nepali: 'नेपाली',
  kathmandu: 'काठमाडौं',
  kavita: 'कविता',
  katha: 'कथा',
  nibandha: 'निबन्ध',
  lekhak: 'लेखक',
  sahitya: 'साहित्य',
  bhasa: 'भाषा',
  bhasha: 'भाषा',
  maya: 'माया',
  sathi: 'साथी',
  saathi: 'साथी',
  ama: 'आमा',
  aama: 'आमा',
  buwa: 'बुवा',
  didi: 'दिदी',
  bhai: 'भाइ',
  dai: 'दाइ',
  ghar: 'घर',
  gaun: 'गाउँ',
  sahar: 'सहर',
  desh: 'देश',
  himal: 'हिमाल',
  pahad: 'पहाड',
  nadi: 'नदी',
  paani: 'पानी',
  pani: 'पानी',
  hawa: 'हावा',
  batas: 'बतास',
  akash: 'आकाश',
  jun: 'जुन',
  tara: 'तारा',
  surya: 'सूर्य',
  bihana: 'बिहान',
  sanjh: 'साँझ',
  raat: 'रात',
  rat: 'रात',
  din: 'दिन',
  samaya: 'समय',
  jindagi: 'जिन्दगी',
  jiwan: 'जीवन',
  mutu: 'मुटु',
  man: 'मन',
  sapana: 'सपना',
  aansu: 'आँसु',
  hasi: 'हाँसी',
  khusi: 'खुसी',
  dukha: 'दुःख',
  sukha: 'सुख',
  prem: 'प्रेम',
  namaste: 'नमस्ते',
  dhanyabad: 'धन्यवाद',
  swagatam: 'स्वागतम्',
  ho: 'हो',
  hoina: 'होइन',
  chha: 'छ',
  chhaina: 'छैन',
  ma: 'म',
  timi: 'तिमी',
  tapai: 'तपाईं',
  hami: 'हामी',
  uni: 'उनी',
  mero: 'मेरो',
  timro: 'तिम्रो',
  hamro: 'हाम्रो',
  yo: 'यो',
  tyo: 'त्यो',
  ani: 'अनि',
  ra: 'र',
  tar: 'तर',
  kina: 'किन',
  kasari: 'कसरी',
  kahan: 'कहाँ',
  kaha: 'कहाँ',
  kalam: 'कलम',
  press: 'प्रेस',
  aba: 'अब',
  pheri: 'फेरि',
  sadhai: 'सधैं',
  ajha: 'अझै',
  sabai: 'सबै',
  eklai: 'एक्लै',
  banna: 'बन्न',
  lekhne: 'लेख्ने',
  padhne: 'पढ्ने',
  bolne: 'बोल्ने',
  sunne: 'सुन्ने',
  // Very common given names. Rules alone give रम / सित because the roman
  // shorthand drops the long vowels these names actually carry; the lookup is
  // case-insensitive, so "Ram" and "ram" both land here.
  ram: 'राम',
  sita: 'सीता',
  gita: 'गीता',
  hari: 'हरि',
  shyam: 'श्याम',
  krishna: 'कृष्ण',
  laxmi: 'लक्ष्मी',
  bishnu: 'विष्णु',
  saraswati: 'सरस्वती',
  kumar: 'कुमार',
  devi: 'देवी',
};

/**
 * Postpositions that attach to a word without killing the preceding vowel.
 *
 * Nepali writes मनको, not मन्को — the न keeps its inherent 'a' because "ko" is a
 * suffix, not the next syllable of the stem. The generic rule (consonant +
 * consonant takes a halanta) is right for सुन्दर and गर्छ but wrong for every
 * word carrying one of these, which is a very large share of ordinary prose.
 *
 * Only matched when the suffix is the entire remainder of the word, so "dar" in
 * "sundar" is untouched.
 */
const SUFFIXES = new Set([
  'ko',
  'ka',
  'kaa',
  'ki',
  'le',
  'lai',
  'laai',
  'ma',
  'maa',
  'bata',
  'baata',
  'sanga',
  'sangai',
  'sang',
  'haru',
  'harule',
  'harulai',
  'mai',
  'mata',
]);

/**
 * How each postposition is actually spelt, for the stem lookup below.
 *
 * A separate table from `SUFFIXES` because the two answer different questions:
 * that set decides whether a halanta is suppressed, and needs every spelling a
 * writer might type; this one has to *produce* Devanagari, so it carries only
 * the suffixes whose correct form is known — हरू, not the हरु the letter-by-
 * letter rules would give. A test asserts these keys are a subset of `SUFFIXES`.
 */
const SUFFIX_FORMS: Record<string, string> = {
  ko: 'को',
  ka: 'का',
  kaa: 'का',
  ki: 'की',
  le: 'ले',
  lai: 'लाई',
  laai: 'लाई',
  ma: 'मा',
  maa: 'मा',
  mai: 'मै',
  bata: 'बाट',
  baata: 'बाट',
  sang: 'सँग',
  sanga: 'सँग',
  sangai: 'सँगै',
  haru: 'हरू',
  harule: 'हरूले',
  harulai: 'हरूलाई',
};

const SUFFIX_FORM_KEYS = Object.keys(SUFFIX_FORMS).sort((a, b) => b.length - a.length);

/** Exported for the drift test in unicode.test.ts; not part of the API. */
export const SUFFIX_FORMS_FOR_TEST = SUFFIX_FORMS;
export const SUFFIXES_FOR_TEST = SUFFIXES;

/**
 * Shortest stem the suffix lookup will accept.
 *
 * Two characters is too few: "mama" would be read as the dictionary word "ma"
 * (म) plus the postposition -ma, giving ममा for what is plainly मामा. Three
 * keeps every real case — ram, ghar, pani, sathi — and drops the coincidences.
 */
const MIN_STEM = 3;

/**
 * The dictionary, reached through an inflected form as well as a bare one.
 *
 * The dictionary only ever matched whole words, so it rescued `sathi` → साथी
 * and then abandoned `sathiko` to the letter rules, which give सथिको — the
 * long vowel the shorthand drops is exactly what the dictionary exists to
 * restore, and postpositions are attached to a very large share of ordinary
 * Nepali prose. Longest suffix first, so `harule` is not read as `le`.
 */
function dictionaryLookup(lower: string): string | null {
  const direct = DICTIONARY[lower];
  if (direct) return direct;

  for (const suffix of SUFFIX_FORM_KEYS) {
    if (!lower.endsWith(suffix)) continue;
    const stem = lower.slice(0, -suffix.length);
    if (stem.length < MIN_STEM) continue;

    const known = DICTIONARY[stem];
    if (known) return known + SUFFIX_FORMS[suffix];
  }

  return null;
}

const CONSONANT_KEYS = keysByLengthDesc(CONSONANTS);
const VOWEL_KEYS = keysByLengthDesc(VOWELS);

function keysByLengthDesc(table: Record<string, unknown>): string[] {
  return Object.keys(table).sort((a, b) => b.length - a.length || a.localeCompare(b));
}

function matchLongest(input: string, at: number, keys: string[]): string | null {
  for (const key of keys) {
    if (input.startsWith(key, at)) return key;
  }
  return null;
}

/**
 * Capitals whose meaning is dropped at the start of a word.
 *
 * Capitals normally select the retroflex series (T=ट, D=ड), but people
 * capitalize names out of habit — "Prabesh", "Ram", "Sita" — and that habit
 * collides with the convention. Phonotactics resolves most of it: ड़, ष and ण
 * never begin a Nepali word, so a leading R/S/N is certainly just capitalization.
 *
 * The long vowels are here for a different reason: "Anil" is far more likely to
 * be अनिल than आनिल, and anyone who wants आ has the unambiguous "aa" spelling.
 *
 * T, Th, D, Dh and Ch stay meaningful everywhere — ट, ठ, ड, ढ and छ all begin
 * words regularly (ठूलो, ढोका), so "Thulo" must stay ठुलो.
 */
const WORD_INITIAL_PLAIN = new Set(['R', 'Rh', 'S', 'Sh', 'N', 'A', 'I', 'U']);

/**
 * Longest match at `at`, tolerating stray capitalization.
 *
 * An exact (case-sensitive) match wins, because that is how the retroflex series
 * is selected — except at the start of a word for the keys above, and except
 * where the capital means nothing at all (there is no "P" key, so "Prabesh" has
 * to fall back to "p" or the letter survives untransliterated).
 */
function matchKey(
  word: string,
  lower: string,
  at: number,
  keys: string[],
  atWordStart: boolean,
): string | null {
  const exact = matchLongest(word, at, keys);
  if (exact && !(atWordStart && WORD_INITIAL_PLAIN.has(exact))) return exact;
  return matchLongest(lower, at, keys) ?? exact;
}

export interface TransliterateOptions {
  /** Convert ASCII digits to Devanagari numerals (०१२). Off by default: years
   *  and counts are usually wanted as typed. */
  digits?: boolean;
  /** Convert a full stop to the danda (।). On by default for literary prose. */
  danda?: boolean;
  /** Skip the common-word dictionary and use the rules alone. */
  rulesOnly?: boolean;
}

/**
 * Transliterates a single word (no whitespace). Text already in Devanagari is
 * passed through untouched, so re-running conversion is idempotent.
 */
export function transliterateWord(word: string, options: TransliterateOptions = {}): string {
  const { digits = false, danda = true, rulesOnly = false } = options;
  if (!word) return word;

  // Case-sensitive: "OM" is the symbol, "om" is the name ओम.
  const symbol = SYMBOL_WORDS[word];
  if (symbol) return symbol;

  if (!rulesOnly) {
    const known = dictionaryLookup(word.toLowerCase());
    if (known) return known;
  }

  let out = '';
  let index = 0;
  /** True right after a consonant, i.e. a matra (not an independent vowel) applies. */
  let afterConsonant = false;
  const lower = word.toLowerCase();

  while (index < word.length) {
    const char = word[index];

    // Devanagari, spaces and anything we do not own passes straight through.
    if (char >= 'ऀ' && char <= 'ॿ') {
      out += char;
      afterConsonant = false;
      index += 1;
      continue;
    }

    // M and H are marks that attach to a preceding syllable (सुनं, दुःख), so at
    // the start of a word they cannot be marks at all — there is nothing to
    // attach to. Without this, "Manisha" opens on a stray anusvara and "Hari"
    // on a visarga. The punctuation signs have no such ambiguity.
    const sign = SIGNS[char];
    const isAttachingMark = char === 'M' || char === 'H';
    if (sign !== undefined && !(isAttachingMark && index === 0)) {
      out += sign;
      afterConsonant = false;
      index += 1;
      continue;
    }

    if (char >= '0' && char <= '9') {
      out += digits ? DEVANAGARI_DIGITS[Number(char)] : char;
      afterConsonant = false;
      index += 1;
      continue;
    }

    if (char === '.') {
      out += danda ? '।' : '.';
      afterConsonant = false;
      index += 1;
      continue;
    }

    const atWordStart = index === 0;
    const consonant = matchKey(word, lower, index, CONSONANT_KEYS, atWordStart);
    const leadingVowel = matchKey(word, lower, index, VOWEL_KEYS, atWordStart);

    // A longer vowel match wins the position: "Rituraj" must open on ऋ (the
    // two-character "Ri"), not on ड़ + ि from the single-character "R".
    if (consonant && (!leadingVowel || leadingVowel.length <= consonant.length)) {
      out += CONSONANTS[consonant];
      index += consonant.length;

      const vowel = matchKey(word, lower, index, VOWEL_KEYS, false);
      if (vowel) {
        out += VOWELS[vowel][1];
        index += vowel.length;
        afterConsonant = false;
      } else {
        // No vowel: halanta joins it to the next consonant, but a word-final
        // consonant keeps its bare form (नेपाल, not नेपाल्).
        //
        // The lookahead must ignore marks. Case-insensitive matching would
        // otherwise read the M of "sunM" as the consonant म and emit सुन्ं
        // instead of सुनं.
        const atWordEnd = index >= word.length;
        const nextIsMark = !atWordEnd && SIGNS[word[index]] !== undefined;
        const nextIsConsonant =
          !atWordEnd && !nextIsMark && matchKey(word, lower, index, CONSONANT_KEYS, false) !== null;
        // …unless what follows is a postposition, which attaches to the word
        // rather than continuing the stem: मनको, not मन्को.
        const followedBySuffix = SUFFIXES.has(lower.slice(index));
        if (nextIsConsonant && !followedBySuffix) out += HALANTA;
        afterConsonant = true;
      }
      continue;
    }

    if (leadingVowel) {
      const [independent, matra] = VOWELS[leadingVowel];
      out += afterConsonant ? matra : independent;
      index += leadingVowel.length;
      afterConsonant = false;
      continue;
    }

    out += char;
    afterConsonant = false;
    index += 1;
  }

  return out;
}

/** Characters that belong to a roman "word" for conversion purposes. */
const LETTER_RUN = /^[A-Za-z0-9\\~^]+/;
const PUNCTUATION_RUN = /^[^A-Za-z0-9\\~^\s]+/;

/**
 * True for a token that is letters and nothing else.
 *
 * The alternatives picker offers readings of a *word*, so it stands aside for
 * anything carrying punctuation: `ho.` is one token to the converter but two
 * decisions, and a list offering four spellings of the danda would be noise.
 */
export function isPlainToken(token: string): boolean {
  return token.length > 0 && LETTER_RUN.exec(token)?.[0].length === token.length;
}

/**
 * Punctuation, longest match first.
 *
 * Nepali ends a sentence with a danda, not a full stop, and doubles it to close
 * a verse. Typing those directly needs a Devanagari keyboard, so the roman
 * shorthand has to carry them.
 */
const PUNCTUATION: [string, string][] = [
  ['...', '…'],
  ['..', '॥'],
  ['.', '।'],
];

function convertPunctuation(run: string, danda: boolean): string {
  if (!danda) return run;

  let out = '';
  let index = 0;

  outer: while (index < run.length) {
    for (const [roman, devanagari] of PUNCTUATION) {
      if (run.startsWith(roman, index)) {
        out += devanagari;
        index += roman.length;
        continue outer;
      }
    }
    out += run[index];
    index += 1;
  }

  return out;
}

/**
 * Converts one whitespace-delimited token, letters and punctuation together.
 *
 * This exists because word-at-a-time conversion has to cope with the punctuation
 * stuck to the word. Matching only trailing letters means "ho." converts
 * nothing at all — neither the word nor the stop — which is the single most
 * common thing anyone types.
 */
export function transliterateSegment(token: string, options: TransliterateOptions = {}): string {
  if (!token) return token;
  const { danda = true } = options;

  let out = '';
  let rest = token;

  while (rest.length > 0) {
    const letters = LETTER_RUN.exec(rest);
    if (letters) {
      out += transliterateWord(letters[0], options);
      rest = rest.slice(letters[0].length);
      continue;
    }

    const punctuation = PUNCTUATION_RUN.exec(rest);
    if (punctuation) {
      out += convertPunctuation(punctuation[0], danda);
      rest = rest.slice(punctuation[0].length);
      continue;
    }

    out += rest[0];
    rest = rest.slice(1);
  }

  return out;
}

/**
 * Transliterates free text, preserving whitespace exactly so the shape of a
 * stanza survives conversion.
 */
export function transliterate(text: string, options: TransliterateOptions = {}): string {
  if (!text) return text;
  return text
    .split(/(\s+)/)
    .map((chunk) => (/^\s/.test(chunk) ? chunk : transliterateSegment(chunk, options)))
    .join('');
}

/* ---------------------------------------------------------------------------
 * Alternatives
 *
 * The engine above is deterministic: one roman spelling, one Devanagari word.
 * That is the right default and it is also, unavoidably, sometimes wrong —
 * romanized Nepali throws away distinctions the script keeps. `pani` is both
 * पानी and पनि; a lowercase `t` could be त or ट; `i` could be ि or ी. A writer
 * who wanted the other one currently has no way to reach it except by knowing
 * the scheme well enough to spell around it.
 *
 * So the same tables that create the ambiguity are used to enumerate it. Every
 * candidate below is the output of the *same* engine on a spelling one edit away
 * from what was typed, which is what keeps the list explainable — each row
 * differs from the one above it in exactly one decision a reader can name.
 * -------------------------------------------------------------------------*/

/**
 * What a row changed, named in both languages.
 *
 * A pair rather than a translatable string, matching how the literary taxonomy
 * carries its labels: these are grammatical terms attached to data, and the
 * picker picks a side at render time. They are also the whole explanation a row
 * gets, so they name the *ambiguity* — "long vowel", not "changed i to ee".
 */
export interface CandidateNote {
  np: string;
  en: string;
}

export const NOTES = {
  meaning: { np: 'अर्को अर्थ', en: 'other meaning' },
  /** Applied by the shared ranking, not by the rules — see lib/tl-crowd. */
  crowd: { np: 'धेरैले रोजेको', en: 'commonly chosen' },
  literal: { np: 'अक्षरअनुसार', en: 'letter by letter' },
  dictionary: { np: 'शब्दकोश', en: 'dictionary' },
  retroflex: { np: 'मूर्धन्य', en: 'retroflex' },
  long: { np: 'दीर्घ', en: 'long vowel' },
  anusvara: { np: 'अनुस्वार', en: 'anusvara' },
  chandrabindu: { np: 'चन्द्रबिन्दु', en: 'chandrabindu' },
} as const satisfies Record<string, CandidateNote>;

export interface TransliterationCandidate {
  text: string;
  /** Absent on the first row, which is what the engine gives on its own. */
  note?: CandidateNote;
}

/**
 * Homographs: one roman spelling, two real words.
 *
 * These are the cases no rule can settle, because both readings are correct
 * Nepali and only the sentence knows which was meant. The dictionary already
 * picks the commoner one; this is how the other stays reachable.
 *
 * Deliberately short. A speculative pair here costs every writer a wrong row in
 * a five-row list, so the bar is "both spellings are words a reader would expect
 * to be able to type", not "both are attested somewhere".
 */
const ALTERNATES: Record<string, string[]> = {
  pani: ['पनि'], // पानी water · पनि also
  ma: ['मा'], // म I · मा in
  tara: ['तर'], // तारा star · तर but
  jun: ['जून'], // जुन which · जून moon
  kam: ['काम'], // कम less · काम work
  kal: ['काल'], // कल machine · काल time, death
  bal: ['बाल'], // बल strength · बाल child
  man: ['मान'], // मन heart · मान respect
  pal: ['पाल'], // पल moment · पाल tent
};

/**
 * True when both readings of a spelling are real words.
 *
 * The shared ranking asks this before it is allowed to change anyone's default:
 * where the choice is contextual — पानी or पनि, कम or काम — a tally of what
 * other people meant in other sentences is not evidence about this one.
 */
export function hasHomograph(roman: string): boolean {
  return roman.toLowerCase() in ALTERNATES;
}

/**
 * One ambiguity, and the respellings that resolve it differently.
 *
 * `where` is what keeps the list free of nonsense. An edit applied at every
 * matching position produces forms nobody typed: the nasal rule turned `nepal`
 * into मेपल by reading its opening न as a mark, and lengthening the `a` of
 * `sapana` in the middle gave सपान. The constraint is per-ambiguity because the
 * reason differs — a nasal is only a mark when it closes a syllable, while a
 * dropped long `aa` is nearly always the *first* vowel of the word (राम, काम,
 * साथ, बाट).
 */
interface EditRule {
  from: string;
  to: string;
  note: CandidateNote;
  where?: (word: string, at: number) => boolean;
  /** Only the first match, for ambiguities that are one-per-word in practice. */
  firstOnly?: boolean;
}

const VOWEL_LETTERS = new Set(['a', 'e', 'i', 'o', 'u']);

/**
 * A nasal is a candidate mark only when it closes a syllable: after a vowel and
 * before a consonant, as in सम्झना/संझना. At the start of a word there is
 * nothing for it to attach to, and the engine correctly reads it as म or न.
 */
function closesSyllable(word: string, at: number): boolean {
  if (at === 0) return false;
  const before = word[at - 1].toLowerCase();
  const after = word[at + 1]?.toLowerCase();
  return VOWEL_LETTERS.has(before) && after !== undefined && !VOWEL_LETTERS.has(after);
}

const EDIT_RULES: EditRule[] = [
  // Consonants an ASCII keyboard cannot distinguish without the capital.
  { from: 't', to: 'T', note: NOTES.retroflex },
  { from: 'd', to: 'D', note: NOTES.retroflex },
  { from: 'n', to: 'N', note: NOTES.retroflex },
  { from: 'sh', to: 'Sh', note: NOTES.retroflex },
  { from: 's', to: 'S', note: NOTES.retroflex },
  { from: 'r', to: 'R', note: NOTES.retroflex },

  // Vowels whose length the roman shorthand drops. Nepali carries long i and u
  // at the end of a great many words (नदी, साथी, हरू) so those are tried
  // everywhere; a long aa is overwhelmingly the first vowel.
  { from: 'i', to: 'ee', note: NOTES.long },
  { from: 'u', to: 'oo', note: NOTES.long },
  {
    from: 'a',
    to: 'aa',
    note: NOTES.long,
    firstOnly: true,
    // Not next to an `a` that is already there: `aansu` is spelt long already,
    // and a third one only produces आअन्सु.
    where: (word, at) =>
      word[at + 1]?.toLowerCase() !== 'a' && word[at - 1]?.toLowerCase() !== 'a',
  },

  { from: 'n', to: 'M', note: NOTES.anusvara, where: closesSyllable },
  { from: 'm', to: 'M', note: NOTES.anusvara, where: closesSyllable },
];

/**
 * How many rows any one ambiguity may claim.
 *
 * Without this a long word fills the whole list with a single axis — `sundar`
 * offered सुन्डर, सुण्दर and सुन्दड़, three ways of being wrong in the same way,
 * and pushed out the vowel-length row that was actually useful. Two of a kind is
 * enough to show the writer what sort of thing this row is.
 */
const AXIS_QUOTA = 2;

/** Past this there is nothing useful left to enumerate, only cost. */
const MAX_CANDIDATE_INPUT = 24;

/**
 * Every spelling produced by replacing one occurrence of `from` with `to`.
 *
 * One occurrence at a time on purpose. Replacing them all would produce forms
 * that differ from the original in several places at once, which is both less
 * likely to be what the writer meant and impossible to read off the list at a
 * glance.
 */
function singleEdits(word: string, rule: EditRule): string[] {
  const out: string[] = [];
  for (let at = word.indexOf(rule.from); at !== -1; at = word.indexOf(rule.from, at + 1)) {
    if (rule.where && !rule.where(word, at)) continue;
    out.push(word.slice(0, at) + rule.to + word.slice(at + rule.from.length));
    if (rule.firstOnly) break;
  }
  return out;
}

/**
 * The readings of a romanized word, best first.
 *
 * The first entry is always what `transliterateWord` gives on its own, so the
 * picker's default row and the text already on the page cannot disagree.
 * Everything after it is ordered by how often that particular ambiguity is the
 * one that was wrong: a homograph (a different word entirely), then the
 * dictionary/rules disagreement, then a consonant the capital would have
 * changed, then vowel length, then a nasal that should have been a mark.
 *
 * Ordering past the first two rows is judgement, not measurement — there is no
 * Nepali frequency corpus behind it. What makes the list useful anyway is that
 * the writer's own choice is remembered (see lib/tl-memory), so the ranking only
 * has to be right once per spelling per writer.
 */
export function transliterationCandidates(
  word: string,
  options: TransliterateOptions = {},
  limit = 5,
): TransliterationCandidate[] {
  const base = transliterateWord(word, options);
  const candidates: TransliterationCandidate[] = [{ text: base }];

  // Nothing was converted (an ASCII word, a URL, already-Devanagari text): there
  // is no ambiguity to offer, and a list of near-identical rows would be noise.
  if (!word || word.length > MAX_CANDIDATE_INPUT || base === word || hasDevanagari(word)) {
    return candidates;
  }

  const seen = new Set([base, word]);
  const lower = word.toLowerCase();
  /** Rows spent per ambiguity, against AXIS_QUOTA. Keyed by the shared object. */
  const spent = new Map<CandidateNote, number>();

  const add = (text: string, note: CandidateNote) => {
    if (candidates.length >= limit || !text || seen.has(text)) return;
    if ((spent.get(note) ?? 0) >= AXIS_QUOTA) return;

    seen.add(text);
    spent.set(note, (spent.get(note) ?? 0) + 1);
    candidates.push({ text, note });
  };

  const addSpelling = (spelling: string, note: CandidateNote) => {
    if (spelling === word) return;
    add(transliterateWord(spelling, options), note);
  };

  for (const alternate of ALTERNATES[lower] ?? []) add(alternate, NOTES.meaning);

  /*
   * The other side of the dictionary.
   *
   * If the dictionary answered, the letter rules are the alternative and vice
   * versa — which is how someone reaches नेपल when they really did mean it, and
   * how a word the dictionary has never heard of still offers its dictionary
   * form when a suffix lookup would have found one.
   */
  const literal = transliterateWord(word, { ...options, rulesOnly: true });
  if (literal !== base) add(literal, NOTES.literal);
  else {
    const known = dictionaryLookup(lower);
    if (known) add(known, NOTES.dictionary);
  }

  for (const rule of EDIT_RULES) {
    for (const spelling of singleEdits(word, rule)) addSpelling(spelling, rule.note);
  }

  // Both long vowels at once — साथीहरू and its like need two edits, and it is
  // common enough to be worth the one exception to the single-edit rule.
  addSpelling(word.replace(/i/g, 'ee').replace(/u/g, 'oo'), NOTES.long);

  // A word-final nasal is very often the chandrabindu: गाउँ, हुँ.
  if (/[nm]$/.test(word)) addSpelling(`${word.slice(0, -1)}~`, NOTES.chandrabindu);

  return candidates;
}

/**
 * Marks and symbols that have no roman spelling, offered as a palette.
 *
 * Everything here is either unreachable from a roman keyboard or ambiguous
 * enough that guessing would be worse than picking.
 */
export const SPECIAL_CHARACTERS: { char: string; name: string; hint?: string }[] = [
  { char: '।', name: 'पूर्णविराम', hint: '.' },
  { char: '॥', name: 'द्वि-पूर्णविराम', hint: '..' },
  { char: 'ॐ', name: 'ओम्', hint: 'OM' },
  { char: 'ँ', name: 'चन्द्रबिन्दु', hint: '~' },
  { char: 'ं', name: 'अनुस्वार', hint: 'M' },
  { char: 'ः', name: 'विसर्ग', hint: 'H' },
  { char: '्', name: 'हलन्त', hint: '\\' },
  { char: 'ऽ', name: 'अवग्रह' },
  { char: '़', name: 'नुक्ता' },
  // Loanword vowels — no typing key, see CANDRA note above.
  { char: 'ऑ', name: 'ऑ (चन्द्र ओ)' },
  { char: 'ॉ', name: 'ॉ मात्रा' },
  { char: 'ऍ', name: 'ऍ (चन्द्र ए)' },
  { char: 'ॅ', name: 'ॅ मात्रा' },
  { char: 'ॠ', name: 'दीर्घ ऋ', hint: 'RR' },
  { char: 'ळ', name: 'ळ', hint: 'L' },
  /*
   * The joiners. These decide how a cluster *renders* without changing the
   * word: ZWNJ after a halanta keeps the two letters separate (क्‌ष) where the
   * font would otherwise form a ligature, and ZWJ asks for the half-form. Nepali
   * typesetting needs both, and neither can be typed from a roman keyboard.
   */
  { char: '‌', name: 'हलन्त छुट्याउने (ZWNJ)' },
  { char: '‍', name: 'आधा अक्षर (ZWJ)' },
  { char: '₹', name: 'रुपैयाँ', hint: 'RS' },
  { char: '॰', name: 'संक्षेप' },
  { char: '“', name: 'उद्धरण खोल्ने' },
  { char: '”', name: 'उद्धरण बन्द' },
  { char: '—', name: 'लामो डयास' },
  { char: '…', name: 'तीन थोप्ला', hint: '...' },
];

/** True if the string contains at least one Devanagari code point. */
export function hasDevanagari(text: string): boolean {
  return /[ऀ-ॿ]/.test(text);
}

/** Digits only, for reading-time badges and counters: 12 → १२. */
export function toDevanagariDigits(value: number | string): string {
  return String(value).replace(/[0-9]/g, (d) => DEVANAGARI_DIGITS[Number(d)]);
}

/**
 * A cheat-sheet for the editor's help popover — kept next to the tables it
 * documents so the two cannot drift.
 */
export const TRANSLITERATION_HINTS: { roman: string; devanagari: string; note: string }[] = [
  { roman: 'nepal', devanagari: 'नेपाल', note: 'word-final consonant keeps its bare form' },
  { roman: 'namaste', devanagari: 'नमस्ते', note: 'consonant + consonant takes a halanta' },
  { roman: 'kaathmaandau', devanagari: 'काठमाण्डौ', note: 'double the vowel to lengthen it' },
  { roman: 'Thulo', devanagari: 'ठुलो', note: 'capitals give the retroflex series (T, Th, D)' },
  { roman: 'Prabesh', devanagari: 'प्रबेश', note: 'names may be capitalised as usual' },
  { roman: 'ho.', devanagari: 'हो।', note: 'a full stop becomes a danda' },
  { roman: 'ho..', devanagari: 'हो॥', note: 'two stops close a verse' },
  { roman: 'saa~jh', devanagari: 'साँझ', note: '~ adds chandrabindu, M adds anusvara' },
  { roman: 'manko', devanagari: 'मनको', note: 'ko, le, maa… attach without a halanta' },
  { roman: 'sathi\\', devanagari: 'साथि्', note: 'trailing \\ forces a halanta' },
  { roman: 'Rituraj', devanagari: 'ऋतुरज', note: 'Ri gives ऋ; plain ri stays र + ि' },
  { roman: 'OM', devanagari: 'ॐ', note: 'capitals only — "om" stays the name ओम' },
];
